// includes/auth/liveApi.js
//
// Family Live data (server: elakay_routes/liveActivity.js).
// The stream is Server-Sent Events read through fetch() -- EventSource can't
// send the Authorization header. The first message of every topic is a full
// server snapshot, so (re)connecting IS the resync: the browser never keeps
// its own running total.
import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";

const getToken = () => isAuthenticated()?.token || "";

const query = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, Array.isArray(v) ? v.join(",") : String(v)); });
  const s = q.toString();
  return s ? `?${s}` : "";
};

const getJson = async (path, params, { signal } = {}) => {
  const response = await fetch(`${elakay_API}${path}${query(params)}`, { headers: { Accept: "application/json", Authorization: `Bearer ${getToken()}` }, signal });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(data.error || `Live request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
};

const sendJson = async (path, method, body) => {
  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(data.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.errors = Array.isArray(data.errors) ? data.errors : undefined;
    throw error;
  }
  return data;
};

export const getWallsAPI = (params, opts) => getJson("/family/community/walls", params, opts);
// Activity History (one MongoDB document per day).
export const getActivityHistoryAPI = (params, opts) => getJson("/family/community/activity-history", params, opts);
export const getActivityHistorySettingsAPI = (opts) => getJson("/family/community/activity-history/settings", {}, opts);
export const updateActivityHistorySettingsAPI = (patch) => sendJson("/family/community/activity-history/settings", "PATCH", patch);
export const cleanActivityHistoryAPI = (body) => sendJson("/family/community/activity-history/clean", "POST", body);
export const getScopeStatsAPI = (params, opts) => getJson("/family/community/scope-stats", params, opts);
export const getLiveSnapshotAPI = (params, opts) => getJson("/family/live/snapshot", params, opts);

// Individual Visitor & User Activity (one row per browser; Family only).
const visitorPath = (vk, sub = "") => `/family/activity/individual/visitors/${encodeURIComponent(vk)}${sub}`;
export const getIndividualSettingsAPI = (opts) => getJson("/family/activity/individual/settings", {}, opts);
export const updateIndividualSettingsAPI = (patch) => sendJson("/family/activity/individual/settings", "PATCH", patch);
export const getIndividualSummaryAPI = (params, opts) => getJson("/family/activity/individual/summary", params, opts);
export const getIndividualVisitorsAPI = (params, opts) => getJson("/family/activity/individual/visitors", params, opts);
export const getIndividualVisitorAPI = (vk, opts) => getJson(visitorPath(vk), {}, opts);
export const getIndividualHistoryPageAPI = (vk, params, opts) => getJson(visitorPath(vk, "/historypage"), params, opts);
export const getIndividualHistoryLogAPI = (vk, params, opts) => getJson(visitorPath(vk, "/historylog"), params, opts);
export const getIndividualSessionsAPI = (vk, params, opts) => getJson(visitorPath(vk, "/sessions"), params, opts);
export const getIndividualLoginsAPI = (vk, params, opts) => getJson(visitorPath(vk, "/logins"), params, opts);
export const getIndividualSecurityAPI = (vk, opts) => getJson(visitorPath(vk, "/security"), {}, opts);
export const addIndividualAccountLinkAPI = (body) => sendJson("/family/activity/individual/account-links", "POST", body);
export const removeIndividualAccountLinkAPI = (id, body) => sendJson(`/family/activity/individual/account-links/${encodeURIComponent(id)}`, "DELETE", body);
// "By user" view: one row per account, grouping its browsers and sessions.
export const getIndividualAccountsAPI = (params, opts) => getJson("/family/activity/individual/accounts", params, opts);
export const getIndividualAccountDetailAPI = (userId, params, opts) => getJson(`/family/activity/individual/accounts/${encodeURIComponent(userId)}`, params, opts);

// Parses an SSE byte stream; calls onEvent(eventName, parsedData) per message.
export const parseSseChunk = (buffer, onEvent) => {
  const parts = buffer.split("\n\n");
  const rest = parts.pop();
  parts.forEach((block) => {
    let event = "message";
    const data = [];
    block.split("\n").forEach((line) => {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    });
    if (!data.length) return;
    try { onEvent(event, JSON.parse(data.join("\n"))); } catch { /* ignore malformed */ }
  });
  return rest;
};

/**
 * Opens one stream. Resolves when the stream ends; rejects on HTTP errors
 * (error.status set, e.g. 401/403/400 -> not retried by the hook).
 */
export const openLiveStream = async ({ params, signal, onEvent, onOpen }) => {
  const response = await fetch(`${elakay_API}/family/live/stream${query(params)}`, {
    headers: { Accept: "text/event-stream", Authorization: `Bearer ${getToken()}` },
    signal,
    cache: "no-store",
  });
  if (!response.ok || !response.body) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.error || `Live stream failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer = parseSseChunk(buffer + decoder.decode(value, { stream: true }), onEvent);
  }
};
